import React, { useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDownIcon,
  HomeIcon,
  LogInIcon,
  LogOutIcon,
  UserIcon,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { supabase } from "../lib/supabaseClient";

const isMissingSessionError = (error) => {
  if (!error) return false;

  return (
    error.name === "AuthSessionMissingError" ||
    error.code === "session_not_found" ||
    error.message?.toLowerCase().includes("auth session missing")
  );
};

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [tournamentTitle, setTournamentTitle] = useState("");

  const menuRef = useRef(null);

  const isStartPage = location.pathname === "/";
  const tournamentMatch = matchPath(
    "/turnering/:id",
    location.pathname
  );
  const tournamentId = tournamentMatch?.params?.id;

  useEffect(() => {
    const fetchUsername = async () => {
      if (!user) {
        setUsername("");
        return;
      }

      const { data, error } = await supabase
        .from("users")
        .select("username")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Kunde inte hämta användarnamn:", error);
        setUsername("");
        return;
      }

      setUsername(data?.username || "");
    };

    fetchUsername();
  }, [user]);

  useEffect(() => {
    const fetchTournamentTitle = async () => {
      if (!tournamentId) {
        setTournamentTitle("");
        return;
      }

      const { data, error } = await supabase
        .from("tournaments")
        .select("name")
        .eq("id", tournamentId)
        .maybeSingle();

      if (error) {
        console.error(
          "Kunde inte hämta turneringens namn:",
          error
        );
        setTournamentTitle("Turnering");
        return;
      }

      setTournamentTitle(data?.name || "Turnering");
    };

    fetchTournamentTitle();
  }, [tournamentId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    setMenuOpen(false);

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (
        sessionError &&
        !isMissingSessionError(sessionError)
      ) {
        throw sessionError;
      }

      if (session) {
        const { error: signOutError } =
          await supabase.auth.signOut({
            scope: "local",
          });

        if (
          signOutError &&
          !isMissingSessionError(signOutError)
        ) {
          throw signOutError;
        }
      }
    } catch (error) {
      if (!isMissingSessionError(error)) {
        console.error("Kunde inte logga ut:", error);
      }
    } finally {
      /*
       * Full omladdning rensar även eventuell gammal user-state
       * i React om Supabase-sessionen redan har försvunnit.
       */
      window.location.replace("/login");
    }
  };

  return (
    <header
      className="sticky top-0 z-[100] isolate w-full border-b border-[#ded8cb] bg-[#fffdf8]/95 shadow-sm backdrop-blur-md"
      style={{ minHeight: "64px" }}
    >
      <div className="grid min-h-16 w-full grid-cols-[minmax(70px,1fr)_minmax(0,2fr)_minmax(70px,1fr)] items-center gap-2 px-3 sm:grid-cols-[160px_minmax(0,1fr)_160px] sm:px-6 lg:px-8">
        {/* Vänster */}
        <div className="flex min-w-0 items-center justify-start">
          {!isStartPage && (
            <button
              type="button"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-[#68716c] transition hover:bg-[#f3f0e7] hover:text-[#183d2d]"
            >
              <HomeIcon className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">Start</span>
            </button>
          )}
        </div>

        {/* Mitten */}
        <div className="relative z-10 min-w-0 text-center">
          {tournamentTitle ? (
            <>
              <h1
                className="truncate text-sm font-semibold tracking-tight text-[#183d2d] sm:text-xl"
                title={tournamentTitle}
              >
                {tournamentTitle}
              </h1>

              <button
                type="button"
                onClick={() => navigate("/")}
                className="mt-0.5 inline-block max-w-full cursor-pointer truncate text-[9px] font-semibold uppercase tracking-[0.18em] text-[#9a7735] transition hover:text-[#183d2d] hover:underline"
                aria-label="Gå till startsidan"
              >
                HoleInOne.se
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/")}
              className="max-w-full cursor-pointer truncate text-sm font-semibold tracking-tight text-[#183d2d] transition hover:text-[#9a7735] hover:underline sm:text-xl"
              aria-label="Gå till startsidan"
            >
              HoleInOne.se
            </button>
          )}
        </div>

        {/* Höger */}
        <div className="flex min-w-0 items-center justify-end">
          {user ? (
            <div className="relative min-w-0" ref={menuRef}>
              <button
                type="button"
                onClick={() =>
                  setMenuOpen((previous) => !previous)
                }
                className={`flex max-w-full items-center gap-1.5 rounded-xl px-2 py-2 text-sm font-medium transition sm:gap-2 sm:px-3 ${
                  menuOpen
                    ? "bg-[#e5ede7] text-[#183d2d]"
                    : "text-[#4f5c55] hover:bg-[#f3f0e7] hover:text-[#183d2d]"
                }`}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
                  <UserIcon className="h-3.5 w-3.5" />
                </span>

                <span className="max-w-[58px] truncate text-xs sm:max-w-[105px] sm:text-sm lg:max-w-[130px]">
                  {username || "Profil"}
                </span>

                <ChevronDownIcon
                  className={`h-3.5 w-3.5 shrink-0 text-[#8a8f8b] transition-transform ${
                    menuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 z-[110] mt-2 w-52 overflow-hidden rounded-xl border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_14px_36px_rgba(31,45,38,0.18)]"
                  role="menu"
                >
                  <div className="border-b border-[#e4ded1] px-4 py-3">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9a7735]">
                      Inloggad som
                    </p>
                    <p className="mt-1 truncate text-sm font-semibold text-[#1f2d26]">
                      {username || "Profil"}
                    </p>
                  </div>

                  <div className="p-1.5">
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-[#a0443e] transition hover:bg-[#f7ebe8]"
                      role="menuitem"
                    >
                      <LogOutIcon className="h-4 w-4" />
                      Logga ut
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="inline-flex items-center gap-2 rounded-xl bg-[#183d2d] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#214d39] sm:text-sm"
            >
              <LogInIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Logga in</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}